// src/components/MyResearch/RequestInvestigationModal.tsx
import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
  Trash,
  Plus,
  Globe,
  Link2,
  MessageSquare,
  Building2,
  Loader2,
  Info,
} from "lucide-react";
import { createInvestigation } from "../../api/investigationsService";
import { useTranslation } from "react-i18next";

interface RequestInvestigationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const RequestInvestigationModal: React.FC<RequestInvestigationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const [company_name, setCompanyName] = useState("");
  const [domains, setDomains] = useState<string[]>([""]);
  const [urls, setUrls] = useState<string[]>([""]);
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ————— helpers —————
  const cleanList = (arr: string[]) =>
    arr.map((s) => s.trim()).filter((s) => s.length > 0);

  const addRow = (setter: React.Dispatch<React.SetStateAction<string[]>>) =>
    setter((prev) => [...prev, ""]);

  const changeRow =
    (index: number, setter: React.Dispatch<React.SetStateAction<string[]>>) =>
    (value: string) =>
      setter((prev) => {
        const copy = [...prev];
        copy[index] = value;
        return copy;
      });

  const removeRow =
    (index: number, setter: React.Dispatch<React.SetStateAction<string[]>>) =>
    () =>
      setter((prev) => prev.filter((_, i) => i !== index));

  // split por coma/espacio/nueva línea al pegar
  const splitPaste =
    (setter: React.Dispatch<React.SetStateAction<string[]>>) =>
    (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const text = e.clipboardData.getData("text");
      if (!text.includes(",") && !text.includes("\n") && !text.includes(" ")) return;
      e.preventDefault();
      const parts = text
        .split(/[\s,\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      setter((prev) => {
        const base = cleanList(prev);
        return [...base, ...parts, ""];
      });
    };

  const domainsCount = useMemo(() => cleanList(domains).length, [domains]);
  const urlsCount = useMemo(() => cleanList(urls).length, [urls]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await createInvestigation(
        company_name.trim(),
        comments.trim(),
        cleanList(domains),
        cleanList(urls)
      );
      toast.success(t("requestInvestigationModal.success"));
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(t("requestInvestigationModal.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {/* overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-[1px] z-50 flex items-center justify-center p-4">
        <DialogContent className="w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-3xl tracking-tight">
              {t("requestInvestigationModal.title")}
            </DialogTitle>
            <DialogDescription className="text-slate-600 leading-relaxed">
              {t("requestInvestigationModal.description")}
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700">
              <Info className="h-4 w-4 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="pt-2">
            {/* grid 2 cols */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Empresa */}
              <div className="md:col-span-2">
                <Label htmlFor="company_name" className="text-sm font-semibold text-slate-700">
                  {t("requestInvestigationModal.companyNameLabel")}
                </Label>
                <div className="mt-2 relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="company_name"
                    placeholder={t("requestInvestigationModal.companyNamePlaceholder")}
                    value={company_name}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                    className="pl-10"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {t("requestInvestigationModal.companyNameHelp")}
                </p>
              </div>

              {/* Dominios */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-slate-700">
                    {t("requestInvestigationModal.domainsLabel")}
                  </Label>
                  <span className="text-xs rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
                    {domainsCount}
                  </span>
                </div>

                <div className="mt-2 space-y-2">
                  {domains.map((domain, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          type="text"
                          value={domain}
                          onChange={(e) => changeRow(index, setDomains)(e.target.value)}
                          onPaste={splitPaste(setDomains)}
                          placeholder="acme.com"
                          className="pl-9"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addRow(setDomains);
                            }
                          }}
                        />
                      </div>
                      {domains.length > 1 && (
                        <Button
                          type="button"
                          onClick={removeRow(index, setDomains)}
                          variant="ghost"
                          className="text-rose-600 hover:text-rose-700"
                          aria-label="remove domain"
                        >
                          <Trash className="h-5 w-5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-2">
                  <Button
                    type="button"
                    onClick={() => addRow(setDomains)}
                    variant="ghost"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                    {t("requestInvestigationModal.addDomain")}
                  </Button>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {t("requestInvestigationModal.domainsHelp")}
                </p>
              </div>

              {/* URLs */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-slate-700">
                    {t("requestInvestigationModal.urlsLabel")}
                  </Label>
                  <span className="text-xs rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
                    {urlsCount}
                  </span>
                </div>

                <div className="mt-2 space-y-2">
                  {urls.map((url, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          type="text"
                          value={url}
                          onChange={(e) => changeRow(index, setUrls)(e.target.value)}
                          onPaste={splitPaste(setUrls)}
                          placeholder="https://app.acme.com"
                          className="pl-9"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addRow(setUrls);
                            }
                          }}
                        />
                      </div>
                      {urls.length > 1 && (
                        <Button
                          type="button"
                          onClick={removeRow(index, setUrls)}
                          variant="ghost"
                          className="text-rose-600 hover:text-rose-700"
                          aria-label="remove url"
                        >
                          <Trash className="h-5 w-5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-2">
                  <Button
                    type="button"
                    onClick={() => addRow(setUrls)}
                    variant="ghost"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                    {t("requestInvestigationModal.addUrl")}
                  </Button>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {t("requestInvestigationModal.urlsHelp")}
                </p>
              </div>

              {/* Comentarios */}
              <div className="md:col-span-2">
                <Label htmlFor="comments" className="text-sm font-semibold text-slate-700">
                  {t("requestInvestigationModal.commentsLabel")}
                </Label>
                <div className="mt-2 relative">
                  <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                  <Textarea
                    id="comments"
                    placeholder={t("requestInvestigationModal.commentsPlaceholder")}
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    className="min-h-[110px] pl-10"
                    onPaste={splitPaste(() => {})}
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {t("requestInvestigationModal.commentsHelp")}
                </p>
              </div>
            </div>

            <DialogFooter className="mt-8 gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              >
                {t("requestInvestigationModal.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={loading || !company_name.trim()}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? t("requestInvestigationModal.sending") : t("requestInvestigationModal.submit")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </div>
    </Dialog>
  );
};

export default RequestInvestigationModal;
